import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { History, SortBy, SortDirection, sortHistory, changeType } from 'src/app/models/history';
import { Store } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subscription, Observable, Subject } from 'rxjs';
import { GetProblemHistory, getProblemHistory } from 'src/app/state-management';

@Component({
    selector: 'app-problem-history',
    templateUrl: './problem-history.component.html',
    styleUrls: ['./problem-history.component.css'],
    standalone: false
})
export class ProblemHistoryComponent implements OnInit, OnDestroy {

  @Input() batchCaseProfilingNumber: string;

  private ngUnsubscribe: Subject<void> = new Subject();
  problemHistory$: Observable<History[]>;
  problemNo: string;
  routeSubscription: Subscription = new Subscription();
  SortBy = SortBy;
  sortBy: SortBy = SortBy.Timestamp;
  SortDirection = SortDirection; // to make the SortDirection type available in the template
  sortDirection: SortDirection = SortDirection.Asc;
  records: History[] = []; // as in historical records...

  getHistoryFired = false;
  getHistoryReturned = false;

  changeTypeMap: Map<string, string> = new Map<string, string>([
    ['NewProblem', 'Case Created'],
    ['ProblemType', 'Type'],
    ['ProblemSubType', 'Subtype'],
    ['ProblemPriority', 'Priority'],
    ['ProblemDescription', 'Description'],
    ['ProblemStatus', 'Status'],
    ['ProblemSource', 'Source'],
    ['ProblemAssignedUser', 'Assigned To'],
    ['ProblemLocation', 'Location'],
    ['ProblemLatLng', 'Coordinates'],
    ['ProblemGroup', 'Group'],
    ['ProblemRole', 'Role'],
    ['NewContact', 'New Contact Added'],
    ['CompanyName', 'Company Name'],
    ['ContactName', 'Contact Name'],
    ['ContactEmail', 'Contact Email'],
    ['ContactPhone', 'Contact Phone'],
    ['ContactAddress', 'Contact Address'],
    ['ChangedContactType', 'Changed Contact Type'],
    ['NewActivity', 'New Activity Added'],
    ['ActivityNote', 'Activity Note'],
    ['ActivityUser', 'Activity Assigned To'],
    ['ActivityFollowUpDate', 'Activity Follow Up Date'],
    ['ActivityDateClosed', 'Activity Completed'],
    ['!ActivityDateClosed', 'Activity Re-opened'],
    ['ActivityInitiatedDate', 'Activity Initiated Date'],
    ['ActivityType', 'Activity Type'],
    ['NewAttachment', 'Document Added'],
    ['AttachmentDeleted', 'Document Deleted'],
    ['AttachmentDescription', 'Document Updated'],
    ['ContactDeleted', 'Contact Deleted'],
    ['ContactPhoneDeleted', 'Contact Phone Deleted'],
    ['ChangedContactPhone', 'Changed Contact Phone'],
    ['ContactNotes', 'Contact Notes'],
    ['NewContactPhone', 'New Contact Phone'],
    ['PhoneDeleted', 'Phone Deleted'],
    ['AttachmentFileType', ''],
    ['AttachmentReassignedFrom', ''],
    ['AttachmentReassignedTo', ''],
    ['NewAsset', 'Asset Added'],
    ['DeleteAsset', 'Asset Deleted'],
    ['CancelWorkOrder', 'Cancel Work Order'],
    ['NewWorkOrder', 'New Work Order'],
    ['CloseWorkRequest', 'Close Work Request'],
    ['IsCancelled', 'Activity Status'],
    ['NewProblemLinked', 'New Linked Case'],
    ['UpdatedLinkedCaseNotes', 'Linked Case Notes'],
    ['UnlinkProblemLink', 'Unlink Case'],
    ['ProblemDuplicated', 'Duplicate case'],
    ['DuplicatedCase', 'Duplicate case']
  ]);

  constructor(
    private store: Store<{}>,
    private route: ActivatedRoute
  ) {
    this.problemHistory$ = this.store.select(getProblemHistory);
  }

  ngOnInit() {
    this.routeSubscription = this.route.queryParams
      .pipe(filter(params => params.ProblemNo))
      .subscribe(params => {
        this.problemNo = params.ProblemNo;
        this.store.dispatch(new GetProblemHistory(this.problemNo));
      });

    this.problemHistory$
      .pipe(
        takeUntil(this.ngUnsubscribe),
      ).subscribe(h => {
        this.records = h;
        this.getHistoryReturned = this.getHistoryFired || h.length > 0;
      });
      
    if (this.batchCaseProfilingNumber) {
      this.problemNo = this.batchCaseProfilingNumber;
      this.store.dispatch(new GetProblemHistory(this.problemNo));
      this.getHistoryFired = true;
    }
    
    this.waitUntilReadyToExport();
  }

  @HostListener('pagehide')
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.routeSubscription.unsubscribe();
  }

  sorted(sortBy: SortBy, sortOrder: SortDirection): boolean {
    return (this.sortBy === sortBy) && (this.sortDirection === sortOrder);
  }

  toggleSort(sortBy: SortBy) {
    if (this.sortBy === sortBy) {
      if (this.sortDirection === SortDirection.Desc) {
        this.sortDirection = SortDirection.Asc;
      } else {
        this.sortDirection = SortDirection.Desc;
      }
    } else {
      this.sortBy = sortBy;
      this.sortDirection = SortDirection.Asc;
    }
    this.records = sortHistory(this.records, this.sortBy, this.sortDirection);
  }

  recordChangeType(record: History): string {
    return changeType(record, this.changeTypeMap);
  }

  isActivity(desc: string) {
    if (desc) {
      return desc.indexOf('Activity Number:') > -1;
    }
    return false;
  }

  getActivityNum(desc: string) {
    let activityNum = '';
    if (desc) {
      const isActivity = desc.indexOf('Activity Number:');
      activityNum = desc.substr(isActivity + 17, 14);
    }
    return activityNum.trim();
  }

  extractDescription(desc: string) {
    let description = '';
    if (desc) {
      const isActivity = desc.indexOf('Activity Number:');
      if (isActivity > -1) {
        const periodPos = desc.indexOf('.');

        description = desc.substr(periodPos + 2);
      } else {
        description = desc;
      }
    }
    return description.trim();
  }

  waitUntilReadyToExport() {
    const interval = setInterval(() => {
      if (this.getHistoryFired && this.getHistoryReturned) {
        const historyLoaded = this.records.length > 0 ? 
          (document.querySelector(`.${this.problemNo}_history .noHistory`) as any) == null :
          true;
        
        if (historyLoaded) {
          clearInterval(interval);

          this.ngUnsubscribe.next();
          this.ngUnsubscribe.complete();
          this.routeSubscription.unsubscribe();
          
          document.dispatchEvent(new Event('batchExport_historyLoaded'));
        }
      }
    }, 25);
  }
}
